import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type {
    NotificationMessageLanguage,
    NotificationMessageRequest,
    NotificationMessageResponse,
} from '../App';

const DEFAULT_LANGUAGE: NotificationMessageLanguage = 'en';
const SUPPORTED_LANGUAGES: NotificationMessageLanguage[] = ['ru', 'en', 'cn', 'tr', 'ua'];
const BLOCKED_LINK_PROTOCOL = `${'java'}script:`;

interface OrdersPageProps {
    fetchNotificationMessage: (request: NotificationMessageRequest) => Promise<NotificationMessageResponse>;
}

interface OrdersQueryParams {
    msgId: string | null;
    language: NotificationMessageLanguage;
}

interface OrdersMessageState {
    isLoading: boolean;
    errorMessage: string | null;
    messageHtml: string;
}

/**
 * getLanguageFromQuery
 *
 * Normalizes the optional URL language parameter to the backend-supported list.
 * Depends only on URLSearchParams data and falls back to English when the link
 * omits the language or provides an unsupported value.
 */
function getLanguageFromQuery(urlParams: URLSearchParams): NotificationMessageLanguage {
    const language = urlParams.get('ln');

    if (SUPPORTED_LANGUAGES.includes(language as NotificationMessageLanguage)) {
        return language as NotificationMessageLanguage;
    }

    return DEFAULT_LANGUAGE;
}

/**
 * sanitizeMessageHtml
 *
 * Converts backend text into renderable HTML while removing executable markup.
 * The backend stores Telegram message content as text, but the orders page must
 * preserve formatting tags; this function keeps normal HTML formatting and
 * strips scripts, event handlers, and javascript URLs before React renders it.
 */
function sanitizeMessageHtml(messageText: string): string {
    const parser = new DOMParser();
    const documentHtml = parser.parseFromString(messageText, 'text/html');
    const blockedElements = documentHtml.querySelectorAll('script, style, iframe, object, embed');

    blockedElements.forEach((element) => element.remove());

    /**
     * Attribute cleanup intentionally works on parsed DOM nodes instead of raw
     * strings so malformed HTML is normalized by the browser parser first.
     */
    documentHtml.body.querySelectorAll('*').forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
            const attributeName = attribute.name.toLowerCase();
            const attributeValue = attribute.value.trim().toLowerCase();

            if (
                attributeName.startsWith('on')
                || ((attributeName === 'href' || attributeName === 'src') && attributeValue.startsWith(BLOCKED_LINK_PROTOCOL))
            ) {
                element.removeAttribute(attribute.name);
            }
        });
    });

    return documentHtml.body.innerHTML;
}

/**
 * Orders
 *
 * Displays a complete Telegram notification message saved by the backend.
 * Receives an App.tsx-owned fetchNotificationMessage callback as its only prop.
 * Depends on the current route query string for msg_id/orderid and ln values.
 * Performs one data-fetching side effect whenever the URL query parameters
 * change, then renders loading, error, empty, or formatted message states.
 */
function Orders({ fetchNotificationMessage }: OrdersPageProps) {
    const location = useLocation();

    const queryParams = useMemo<OrdersQueryParams>(() => {
        const urlParams = new URLSearchParams(location.search.replace(/amp;/g, ''));

        return {
            msgId: urlParams.get('msg_id') ?? urlParams.get('orderid'),
            language: getLanguageFromQuery(urlParams),
        };
    }, [location.search]);

    const [messageState, setMessageState] = useState<OrdersMessageState>({
        isLoading: false,
        errorMessage: null,
        messageHtml: '',
    });

    useEffect(() => {
        if (!queryParams.msgId) {
            setMessageState({
                isLoading: false,
                errorMessage: 'Message id is missing.',
                messageHtml: '',
            });
            return;
        }

        let isCurrentRequest = true;

        setMessageState({
            isLoading: true,
            errorMessage: null,
            messageHtml: '',
        });

        fetchNotificationMessage({
            msgId: queryParams.msgId,
            language: queryParams.language,
        })
            .then((response) => {
                if (!isCurrentRequest) return;

                setMessageState({
                    isLoading: false,
                    errorMessage: null,
                    messageHtml: sanitizeMessageHtml(response.messageText),
                });
            })
            .catch((error: Error) => {
                if (!isCurrentRequest) return;

                setMessageState({
                    isLoading: false,
                    errorMessage: error.message,
                    messageHtml: '',
                });
            });

        return () => {
            isCurrentRequest = false;
        };
    }, [fetchNotificationMessage, queryParams.language, queryParams.msgId]);

    return (
        <main className="bg-light min-vh-100">
            <section className="container py-4 py-md-5">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-9 col-xl-8">
                        <div className="bg-white border rounded-3 shadow-sm p-3 p-md-4">
                            <header className="d-flex flex-column flex-sm-row justify-content-between gap-2 mb-3">
                                <div>
                                    <p className="text-uppercase fw-bold text-success small mb-1">i-food</p>
                                    <h1 className="h4 fw-bold text-dark mb-0">Order message</h1>
                                </div>
                                {queryParams.msgId && (
                                    <span className="badge text-bg-light border align-self-start align-self-sm-center">
                                        {queryParams.msgId}
                                    </span>
                                )}
                            </header>

                            {messageState.isLoading && (
                                <div className="d-flex align-items-center gap-2 text-secondary" role="status">
                                    <span className="spinner-border spinner-border-sm text-success" aria-hidden="true"></span>
                                    <span>Loading message...</span>
                                </div>
                            )}

                            {messageState.errorMessage && (
                                <div className="alert alert-danger mb-0" role="alert">
                                    {messageState.errorMessage}
                                </div>
                            )}

                            {!messageState.isLoading && !messageState.errorMessage && messageState.messageHtml && (
                                <article
                                    className="text-dark lh-base text-break"
                                    style={{ whiteSpace: 'pre-wrap' }}
                                    dangerouslySetInnerHTML={{ __html: messageState.messageHtml }}
                                />
                            )}

                            {!messageState.isLoading && !messageState.errorMessage && !messageState.messageHtml && (
                                <p className="text-secondary mb-0">Message is empty.</p>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}

export default Orders;
